import { useLayoutEffect, useState } from 'react';
import { PanelId, PanelLayout, PANELS, PANEL_GAP, ROW_BUDGET, stackPositions } from './registry';

// The panel stack's motion.
//
// Every change to the layout — a scroll, a move, a panel switched on or off,
// locked or unlocked —
// is animated from where the panels were to where they are now, worked out
// from the two layouts rather than measured: stackPositions gives each
// panel's place in both, and the panels that stay in view slide by the
// difference. A panel crossing an edge of the view slides a short way past
// that edge as it fades and shrinks; one arriving does the same in reverse.
//
// A panel leaving the view has to stay on screen for its fade, so this hands
// back the panels that are leaving and where they were. The app keeps
// rendering them there, frozen — not live — until their fade ends.
//
// The slides are Web Animations on `transform`, which exist only while they
// run, so a panel at rest carries no transform and no stacking context of its
// own (see PanelRow).

/** How long a change takes, in ms. */
export const SLIDE_MS = 450;
/** How small a panel is as it leaves or enters the view. */
const EDGE_SCALE = 0.9;
/** How far a panel slides past the edge of the view as it fades, in dvw, when there is room. */
const EDGE_TRAVEL = 6;
export const EASING = 'cubic-bezier(0.25, 0.8, 0.25, 1)';

// Nothing slides outside the scrolling region's frame plus the 1dvw margin
// it has on each side. On the left that margin is the gap to whatever is
// fixed there — the Key-Center column, or the last locked panel — which a
// sliding panel must not pass over; on the right it is the window edge — the
// page has no horizontal overflow guard, and a panel out there would flash a
// scrollbar.
const FRAME_RIGHT = ROW_BUDGET + PANEL_GAP;

/**
 * How far a panel at `x` slides past the `side` edge of the view: EDGE_TRAVEL,
 * or less where the frame is closer. Measured with the panel at EDGE_SCALE,
 * which is how small it is by the time it is furthest out. `regionLeft` is
 * where the scrolling region starts, past any locked panels.
 */
function edgeShift(x: number, id: PanelId, side: 'left' | 'right', regionLeft: number): number {
  const w = PANELS[id].width;
  if (side === 'right') {
    const room = FRAME_RIGHT - (x + w * (1 + EDGE_SCALE) / 2);
    return Math.max(0, Math.min(EDGE_TRAVEL, room));
  }
  const room = (x + w * (1 - EDGE_SCALE) / 2) - (regionLeft - PANEL_GAP);
  return -Math.max(0, Math.min(EDGE_TRAVEL, room));
}

/** A panel on its way out of the view, drawn at its last place until it has faded. */
export interface LeavingPanel {
  id: PanelId;
  x: number;
}

const panelNode = (id: PanelId) =>
  document.querySelector<HTMLElement>(`.panel-row > [data-panel="${id}"]`);

const shift = (dx: number, scale: number) => `translateX(${dx}dvw) scale(${scale})`;

export function useStackMotion(layout: PanelLayout): LeavingPanel[] {
  const [motion, setMotion] = useState<{
    layout: PanelLayout;
    prev: PanelLayout | null;
    leaving: LeavingPanel[];
  }>({ layout: layout, prev: null, leaving: [] });

  // Worked out during render rather than in an effect, so a panel leaving the
  // view is never unmounted for a frame between its last live render and its
  // fade.
  let current = motion;
  if (motion.layout !== layout) {
    const before = stackPositions(motion.layout);
    const after = stackPositions(layout);
    const still = motion.leaving.filter(l => after.view.indexOf(l.id) === -1);
    const newly = before.view
      .filter(id => after.view.indexOf(id) === -1 && !still.some(l => l.id === id))
      .map(id => ({ id: id, x: before.x[id] as number }));
    current = { layout: layout, prev: motion.layout, leaving: still.concat(newly) };
    setMotion(current);
  }

  useLayoutEffect(() => {
    if (motion.prev === null) return;
    const before = stackPositions(motion.prev);
    const after = stackPositions(motion.layout);
    const reduce = window.matchMedia !== undefined &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const timing = { duration: reduce ? 0 : SLIDE_MS, easing: EASING };

    after.view.forEach(id => {
      const node = panelNode(id);
      if (node === null) return;
      const to = after.x[id] as number;

      if (before.view.indexOf(id) !== -1) {
        const d = (before.x[id] as number) - to;
        if (Math.abs(d) < 0.01) return;
        node.getAnimations().forEach(a => a.cancel());
        node.animate([{ transform: shift(d, 1) }, { transform: shift(0, 1) }], timing);
        return;
      }

      // Arriving: from just past the edge it was waiting beyond, or, for a
      // panel just switched on, in place.
      const side = before.side[id];
      const from = side === undefined ? 0 : edgeShift(to, id, side, after.regionLeft);
      node.getAnimations().forEach(a => a.cancel());
      node.animate([
        { transform: shift(from, EDGE_SCALE), opacity: 0 },
        { transform: shift(0, 1), opacity: 1 },
      ], timing);
    });

    motion.leaving.forEach(l => {
      if (before.view.indexOf(l.id) === -1) return; // already on its way out
      const node = panelNode(l.id);
      if (node === null) return;
      // Out past the edge it now waits beyond, or, switched off, in place.
      const side = after.side[l.id];
      const by = side === undefined ? 0 : edgeShift(l.x, l.id, side, after.regionLeft);
      node.getAnimations().forEach(a => a.cancel());
      const fade = node.animate([
        { transform: shift(0, 1), opacity: 1 },
        { transform: shift(by, EDGE_SCALE), opacity: 0 },
      ], { duration: timing.duration, easing: timing.easing, fill: 'forwards' });
      fade.onfinish = () => {
        setMotion(m => ({ layout: m.layout, prev: m.prev, leaving: m.leaving.filter(p => p.id !== l.id) }));
      };
    });
    // Keyed on the layout alone: `prev` and `leaving` are set in the same
    // update as it, and a fade finishing changes `leaving` without being a
    // new change of layout — rerunning then would replay every slide.
  }, [motion.layout]);

  return current.leaving;
}
